import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import gameService from '../../services/gameService';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import { SearchIcon, EditIcon, TrashIcon, CopyIcon } from '../../components/Icons';

const GameSources = () => {
  const { hasPermission } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination & Search State
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    group_links: [{ name: '', url: '' }],
    is_aggregator: false,
    is_active: true
  });

  const fetchSources = useCallback(async (page = pagination.page, search = searchTerm, limit = pagination.limit) => {
    try {
      setLoading(true);
      const response = await gameService.getSources({ page, limit, search });
      if (response.success) {
        setSources(response.data);
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      } else {
        setError(response.message || 'Failed to fetch provider sources');
      }
    } catch (err) {
      setError('An error occurred while fetching provider sources');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.page, searchTerm]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSources(1, searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const canEdit = hasPermission('sources', 'edit');
  const canDelete = hasPermission('sources', 'delete');
  const showActions = canEdit || canDelete;

  const handleCopyValue = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      alert('Group link copied to clipboard!');
    }).catch(() => {});
  };

  const handlePageChange = (newPage) => {
    fetchSources(newPage, searchTerm);
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));
    fetchSources(1, searchTerm, newLimit);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const normalizeGroupLinks = (links = []) => (
    Array.isArray(links)
      ? links.map((link, index) => (
          typeof link === 'string'
            ? { name: `Link ${index + 1}`, url: link }
            : { name: link?.name || `Link ${index + 1}`, url: link?.url || link?.link || '' }
        ))
      : []
  );

  const handleGroupLinkChange = (field, value, index) => {
    setFormData(prev => {
      const groupLinks = [...(prev.group_links || [{ name: '', url: '' }])];
      groupLinks[index] = { ...(groupLinks[index] || {}), [field]: value };
      return { ...prev, group_links: groupLinks };
    });
  };

  const addGroupLink = () => {
    setFormData(prev => ({
      ...prev,
      group_links: [...(prev.group_links || []), { name: '', url: '' }]
    }));
  };

  const removeGroupLink = (index) => {
    setFormData(prev => {
      const groupLinks = (prev.group_links || []).filter((_, i) => i !== index);
      return { ...prev, group_links: groupLinks.length ? groupLinks : [{ name: '', url: '' }] };
    });
  };

  const resetForm = () => ({
    name: '',
    code: '',
    description: '',
    group_links: [{ name: '', url: '' }],
    is_aggregator: false,
    is_active: true
  });

  const openAddModal = () => {
    setEditId(null);
    setFormData(resetForm());
    setIsModalOpen(true);
  };

  const openEditModal = (source) => {
    setEditId(source._id);
    setFormData({
      name: source.name,
      code: source.code || '',
      description: source.description || '',
      group_links: source.group_links?.length > 0 ? normalizeGroupLinks(source.group_links) : [{ name: '', url: '' }],
      is_aggregator: !!source.is_aggregator,
      is_active: source.is_active
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        group_links: (formData.group_links || [])
          .map((link, index) => {
            if (typeof link === 'string') return { name: `Link ${index + 1}`, url: link.trim() };
            return { name: String(link?.name || '').trim(), url: String(link?.url || '').trim() };
          })
          .filter(link => link.url)
      };
      let response;
      if (editId) {
        response = await gameService.updateSource(editId, payload);
      } else {
        response = await gameService.createSource(payload);
      }

      if (response.success) {
        setIsModalOpen(false);
        setFormData(resetForm());
        setEditId(null);
        fetchSources();
      } else {
        alert(response.message || 'Failed to save provider source');
      }
    } catch (err) {
      // Axios puts server error responses in err.response.data
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while saving provider source');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this provider source? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await gameService.deleteSource(id);
      if (response.success) {
        fetchSources();
      } else {
        alert(response.message || 'Failed to delete provider source');
        setLoading(false);
      }
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      alert(serverMsg || err?.message || 'An error occurred while deleting provider source');
      setLoading(false);
    }
  };

  return (
    <div className="game-sources-page">
      {hasPermission('sources', 'create') && (
        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-primary" onClick={openAddModal}>Add Provider Source</button>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div className="search-container">
          <div className="search-icon-wrapper">
            <SearchIcon />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading && sources.length === 0 ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center' }}>Loading provider sources...</div>
      ) : error ? (
        <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table-striped" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '960px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Code</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Name</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Description</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Group Links</th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Aggregator</th>
                 <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Status</th>
                 {showActions && <th style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Actions</th>}
               </tr>
            </thead>
            <tbody style={{ opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {sources.length === 0 ? (
                <tr>
                  <td colSpan={showActions ? 7 : 6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No provider sources found.</td>
                </tr>
              ) : (
                sources.map((source) => (
                  <tr key={source._id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', fontWeight: 600 }}>{source.code}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{source.name}</td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{source.description || '-'}</td>
                    <td style={{ padding: '1rem', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                      {source.group_links?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', minWidth: '220px' }}>
                          {source.group_links.map((link, index) => (
                            <div key={`${typeof link === 'string' ? link : link.url}-${index}`} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', gap: '0.5rem', padding: '0.45rem 0.55rem', border: '1px solid var(--border-light)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--bg-soft)' }}>
                              <a href={typeof link === 'string' ? link : link.url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', wordBreak: 'break-all', textDecoration: 'none', lineHeight: 1.35 }}>
                                <span style={{ display: 'block', fontWeight: 700, color: 'var(--text-primary)' }}>{typeof link === 'string' ? `Link ${index + 1}` : (link.name || `Link ${index + 1}`)}</span>
                                <span>{typeof link === 'string' ? link : link.url}</span>
                              </a>
                              <button
                                type="button"
                                onClick={() => handleCopyValue(typeof link === 'string' ? link : link.url)}
                                style={{ border: 'none', background: 'transparent', color: 'var(--primary)', cursor: 'pointer', padding: '0.2rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Copy group link"
                              >
                                <CopyIcon size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '9999px',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        backgroundColor: source.is_aggregator ? 'rgba(var(--primary-rgb), 0.12)' : 'rgba(var(--primary-rgb), 0.08)',
                        color: source.is_aggregator ? 'var(--accent)' : 'var(--text-muted)'
                      }}>
                        {source.is_aggregator ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                      <span style={{
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: source.is_active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        color: source.is_active ? 'var(--success)' : 'var(--error)'
                      }}>
                        {source.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                     {showActions && (
                      <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        {canEdit && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => openEditModal(source)}
                          >
                            <EditIcon size={14} />
                            Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn"
                            style={{ padding: '0.4rem 0.8rem', fontSize: '0.75rem', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                            onClick={() => handleDelete(source._id)}
                          >
                            <TrashIcon size={14} />
                            Delete
                          </button>
                        )}
                      </td>
                     )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            loading={loading}
            itemName="provider sources"
          />
        </div>
      )}

      {/* Add/Edit Source Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editId ? "Edit Provider Source" : "Add New Provider Source"}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Source Name *</label>
              <input
                type="text"
                name="name"
                className="input"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g. Asia World Club"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Source Code *</label>
              <input
                type="text"
                name="code"
                className="input"
                value={formData.code}
                onChange={handleInputChange}
                required
                placeholder="e.g. AWC"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Description</label>
            <textarea
              name="description"
              className="input"
              style={{ minHeight: '80px', fontFamily: 'inherit' }}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Brief description of the source"
            />
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500 }}>Group Links</label>
            {(formData.group_links || [{ name: '', url: '' }]).map((link, index) => (
              <div key={`group-link-${index}`} style={{ display: 'grid', gridTemplateColumns: (formData.group_links || []).length > 1 ? 'minmax(140px, 0.35fr) minmax(220px, 1fr) auto' : 'minmax(140px, 0.35fr) minmax(220px, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <input
                  type="text"
                  className="input"
                  value={typeof link === 'string' ? `Link ${index + 1}` : (link.name || '')}
                  onChange={(e) => handleGroupLinkChange('name', e.target.value, index)}
                  placeholder="Name"
                />
                <input
                  type="url"
                  className="input"
                  value={typeof link === 'string' ? link : (link.url || '')}
                  onChange={(e) => handleGroupLinkChange('url', e.target.value, index)}
                  placeholder="URL"
                />
                {(formData.group_links || []).length > 1 && (
                  <button type="button" className="btn btn-secondary" style={{ padding: '0 0.75rem' }} onClick={() => removeGroupLink(index)}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.35rem 0.85rem' }} onClick={addGroupLink}>+ Add Group Link</button>
          </div>
          <div style={{ marginBottom: '1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Is Aggregator</span>
              <label className="switch">
                <input
                  type="checkbox"
                  name="is_aggregator"
                  checked={formData.is_aggregator}
                  onChange={handleInputChange}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg-soft)', borderRadius: 'var(--radius-md)' }}>
              <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Is Active</span>
              <label className="switch">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button type="button" className="btn" onClick={() => setIsModalOpen(false)} disabled={submitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : (editId ? 'Update Provider Source' : 'Create Provider Source')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default GameSources;
